import { Link } from 'react-router-dom';
import { useEffect, useRef, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { ArrowRight, Heart, Users, TrendingUp, Target, ChevronDown } from 'lucide-react';

// Lifestyle photos
const PHOTOS = {
  hero: 'https://images.unsplash.com/photo-1607292684473-c69dcf62cd0b?auto=format&fit=crop&w=2400&q=80',
  conflict: 'https://images.unsplash.com/photo-1655759738595-418970010986?auto=format&fit=crop&w=600&q=80',
  confidence: 'https://images.unsplash.com/photo-1606787503066-794bb59c64bc?auto=format&fit=crop&w=600&q=80',
  goals: 'https://images.unsplash.com/photo-1593819253504-c720de2917f9?auto=format&fit=crop&w=600&q=80',
};

// Fade-in on scroll observer
function useFadeIn() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { el.style.opacity = '1'; el.style.transform = 'translateY(0)'; } },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return ref;
}

function FadeSection({ children, className = '', delay = 0 }) {
  const ref = useFadeIn();
  return (
    <div ref={ref} className={className}
      style={{ opacity: 0, transform: 'translateY(32px)', transition: `opacity 0.8s ease ${delay}s, transform 0.8s ease ${delay}s` }}>
      {children}
    </div>
  );
}

function AnimatedRule({ targetWidth }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) el.style.width = `${targetWidth}px`; },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [targetWidth]);
  return (
    <div ref={ref} style={{ height: '1px', width: '0px', background: '#D4A853', transition: 'width 1.2s ease', margin: '12px auto 16px' }} />
  );
}

export default function Landing() {
  const [navScrolled, setNavScrolled] = useState(false);
  const [scrollY, setScrollY] = useState(0);

  const handleScroll = useCallback(() => {
    const y = window.scrollY;
    setNavScrolled(y > 80);
    setScrollY(y);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif", color: '#1A1A1A' }}>

      {/* Navigation — transparent over hero, solid on scroll */}
      <nav
        className="fixed top-0 left-0 right-0 z-50 px-6 lg:px-12 py-4 flex items-center justify-between"
        style={{
          background: navScrolled ? 'rgba(245,239,230,0.92)' : 'transparent',
          backdropFilter: navScrolled ? 'blur(16px)' : 'none',
          WebkitBackdropFilter: navScrolled ? 'blur(16px)' : 'none',
          borderBottom: navScrolled ? '1px solid rgba(26,26,26,0.06)' : '1px solid transparent',
          transition: 'background 0.4s ease, backdrop-filter 0.4s ease, border-bottom 0.4s ease',
        }}
        data-testid="landing-nav"
      >
        <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
          <img
            src="/logo.png"
            alt="Let's Talk Money"
            className="h-10 w-auto"
            style={{
              filter: navScrolled ? 'none' : 'brightness(10)',
              transition: 'filter 0.4s ease',
            }}
          />
        </Link>
        <div className="flex items-center gap-3">
          <Link to="/auth">
            <Button
              variant="ghost"
              className="hidden sm:inline-flex rounded-full px-5 text-sm font-medium"
              style={{
                color: navScrolled ? '#6B6B6B' : 'rgba(255,255,255,0.8)',
                fontFamily: "'DM Sans', sans-serif",
                transition: 'color 0.4s ease',
              }}
              data-testid="nav-signin"
            >
              Sign In
            </Button>
          </Link>
          <Link to="/auth">
            <Button
              className="rounded-full px-6 py-2.5 text-sm font-semibold shadow-none"
              style={{
                background: navScrolled ? '#1C3D2E' : 'rgba(255,255,255,0.15)',
                color: navScrolled ? '#F5EFE6' : '#FFFFFF',
                backdropFilter: navScrolled ? 'none' : 'blur(8px)',
                border: navScrolled ? 'none' : '1px solid rgba(255,255,255,0.2)',
                fontFamily: "'DM Sans', sans-serif",
                transition: 'background 0.4s ease, color 0.4s ease, border 0.4s ease',
              }}
              data-testid="nav-start"
            >
              Start Free <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </Link>
        </div>
      </nav>

      {/* === HERO — cinematic video with peek-through === */}
      <section className="relative h-screen overflow-hidden" data-testid="hero-section">
        {/* Background video with parallax */}
        <video
          className="absolute inset-0 w-full h-full object-cover"
          src="https://customer-assets.emergentagent.com/job_27d92e08-dfcb-4a20-8559-f527c82c96dd/artifacts/33azabkf_bg.mp4"
          autoPlay
          loop
          muted
          playsInline
          style={{
            willChange: 'transform',
            transform: `translateY(${scrollY * 0.15}px) scale(1.05)`,
            transition: 'transform 0.1s linear',
          }}
        />

        {/* Dark gradient overlay — stronger at bottom for fade into next section */}
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.12) 0%, rgba(0,0,0,0.2) 35%, rgba(0,0,0,0.55) 75%, rgba(0,0,0,0.85) 100%)',
          }}
        />

        {/* Bottom-left text content */}
        <div className="relative h-full flex items-end">
          <div className="w-full px-6 lg:px-14 pb-24 sm:pb-28 lg:pb-32 max-w-3xl">
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl leading-[1.1] font-semibold mb-5"
              style={{
                fontFamily: "'Playfair Display', serif",
                color: '#FFFFFF',
                textShadow: '0 2px 20px rgba(0,0,0,0.3)',
              }}
              data-testid="hero-heading"
            >
              Finally talk about money<br />
              <span style={{ color: '#D4A853' }}>without the stress</span>
            </h1>
            <p
              className="text-base sm:text-lg leading-relaxed mb-8 max-w-lg"
              style={{
                color: 'rgba(255,255,255,0.85)',
                fontFamily: "'DM Sans', sans-serif",
              }}
              data-testid="hero-subtext"
            >
              Practice real financial conversations with an AI partner. Build confidence before the real thing.
            </p>
            <Link to="/auth">
              <Button
                className="rounded-full px-8 py-6 text-base font-semibold shadow-none group"
                style={{
                  background: '#F5EFE6',
                  color: '#1C3D2E',
                  fontFamily: "'DM Sans', sans-serif",
                  transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 30px rgba(0,0,0,0.25)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                data-testid="hero-cta"
              >
                Start a Free Conversation <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>

        {/* Scroll indicator — animated line + chevron */}
        <div
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center cursor-pointer"
          onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
          style={{ opacity: Math.max(0, 1 - scrollY / 200) }}
          data-testid="scroll-indicator"
        >
          <span className="text-xs font-semibold tracking-[0.2em] uppercase mb-3" style={{ color: 'rgba(255,255,255,0.85)', fontFamily: "'DM Sans', sans-serif" }}>Scroll</span>
          <div className="w-[1px] h-8 mb-2 overflow-hidden" style={{ background: 'rgba(255,255,255,0.15)' }}>
            <div className="w-full bg-white animate-scroll-line" style={{ height: '100%' }} />
          </div>
          <ChevronDown className="w-5 h-5 animate-bounce-gentle" style={{ color: 'rgba(255,255,255,0.85)' }} />
        </div>
      </section>

      {/* === YOUTUBE SHORT — featured video === */}
      <section className="py-20 lg:py-28" style={{ background: '#F5EFE6' }} data-testid="youtube-short-section">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <FadeSection>
            <p className="text-center mb-4" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.12em', color: '#8B7355' }}>See It In Action</p>
            <h2 className="text-center mb-3" style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 400, color: '#1A2E1A' }}>
              Watch How It <span style={{ fontStyle: 'italic', color: '#C4613A' }}>Works</span>
            </h2>
            <p className="text-center mx-auto max-w-md mb-10" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '15px', color: '#6B6B6B', lineHeight: 1.6 }}>
              A quick look at what practicing a money conversation feels like
            </p>
          </FadeSection>
          <FadeSection delay={0.15}>
            <div className="flex justify-center">
              <div
                style={{
                  width: 340,
                  maxWidth: '90vw',
                  aspectRatio: '9 / 16',
                  borderRadius: 20,
                  overflow: 'hidden',
                  boxShadow: '0 12px 48px rgba(26,46,26,0.12), 0 2px 8px rgba(0,0,0,0.06)',
                }}
              >
                <iframe
                  src="https://www.youtube.com/embed/JHMbLouUbg4?rel=0"
                  title="Let's Talk Money — See it in action"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  style={{ width: '100%', height: '100%', border: 'none' }}
                  data-testid="youtube-short-embed"
                />
              </div>
            </div>
          </FadeSection>
        </div>
      </section>

      {/* === SECTION A — PRACTICE SCENARIOS === */}
      <section className="py-24 lg:py-32" style={{ background: '#FFFFFF' }} data-testid="practice-scenarios-section">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <FadeSection>
            <p className="text-center mb-4" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.12em', color: '#C4613A' }}>Practice Scenarios</p>
            <h2 className="text-center mb-3" style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(32px, 5vw, 52px)', fontWeight: 300 }}>
              Real Conversations, <span style={{ fontStyle: 'italic', color: '#C4613A' }}>Safe Space</span>
            </h2>
            <p className="text-center mx-auto max-w-lg" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '16px', color: '#6B6B6B' }}>
              Practice the money conversations that matter most in your life
            </p>
          </FadeSection>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" style={{ marginTop: '3rem' }}>
            {[
              { icon: Heart, title: 'Spouse Conversations', desc: 'Navigate budgets, debt, and shared finances with your partner.', delay: 0 },
              { icon: Users, title: 'Family Discussions', desc: 'Talk inheritance, support, and expectations with parents or siblings.', delay: 0.1 },
              { icon: TrendingUp, title: 'Retirement Planning', desc: 'Start the conversation about long-term savings and future security.', delay: 0.2 },
              { icon: Target, title: 'Goal Setting', desc: 'Align on vacation funds, home buying, and major life purchases.', delay: 0.3 },
            ].map((card, i) => (
              <FadeSection key={i} delay={card.delay}>
                <div
                  className="h-full"
                  style={{
                    background: '#F5EFE6',
                    border: '1px solid rgba(28,61,46,0.12)',
                    borderRadius: '16px',
                    padding: '2rem 1.5rem',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(28,61,46,0.10)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                  data-testid={`scenario-card-${i}`}
                >
                  <div style={{ width: '48px', height: '48px', background: '#C4613A', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.25rem' }}>
                    <card.icon className="w-5 h-5 text-white" />
                  </div>
                  <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: '18px', fontWeight: 600, marginBottom: '0.5rem' }}>{card.title}</h3>
                  <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '14px', color: '#6B6B6B', lineHeight: 1.6, marginBottom: '1rem' }}>{card.desc}</p>
                  <Link to="/auth" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '14px', fontWeight: 500, color: '#D4A853', textDecoration: 'none' }} data-testid={`scenario-begin-${i}`}>
                    Begin <span style={{ marginLeft: '2px' }}>&rarr;</span>
                  </Link>
                </div>
              </FadeSection>
            ))}
          </div>
        </div>
      </section>

      {/* === EMOTIONAL OUTCOMES === */}
      <section className="py-24 lg:py-32" style={{ background: '#FFFFFF' }}>
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <FadeSection>
            <p className="text-center text-sm font-semibold tracking-wider uppercase mb-4" style={{ color: '#C4613A' }}>What changes</p>
            <h2 className="text-center text-3xl sm:text-4xl font-semibold mb-16" style={{ fontFamily: "'Playfair Display', serif" }}>
              The conversations that matter most
            </h2>
          </FadeSection>
          <div className="grid md:grid-cols-3 gap-10 lg:gap-14">
            {[
              { img: PHOTOS.conflict, title: 'Less conflict', desc: 'Stop the same argument from happening again.', delay: 0 },
              { img: PHOTOS.confidence, title: 'More confidence', desc: 'Walk into hard conversations knowing what to say.', delay: 0.1 },
              { img: PHOTOS.goals, title: 'Shared goals', desc: 'Get on the same page about the future.', delay: 0.2 },
            ].map((item, i) => (
              <FadeSection key={i} delay={item.delay}>
                <div className="text-center">
                  <img src={item.img} alt={item.title} className="w-full aspect-square object-cover rounded-2xl mb-6 shadow-sm" />
                  <h3 className="text-xl font-semibold mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>{item.title}</h3>
                  <p className="text-base leading-relaxed" style={{ color: '#6B6B6B' }}>{item.desc}</p>
                </div>
              </FadeSection>
            ))}
          </div>
        </div>
      </section>

      {/* === SECTION B — COMMUNICATION SCORING === */}
      <section className="py-24 lg:py-32" style={{ background: '#1C3D2E' }} data-testid="scoring-section">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <FadeSection>
            <p className="text-center mb-4" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.12em', color: '#C4613A' }}>What We Measure</p>
            <h2 className="text-center mb-20" style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(32px, 5vw, 52px)', fontWeight: 300, color: '#F5EFE6' }}>
              How we score your <span style={{ fontStyle: 'italic', color: '#D4A853' }}>communication</span>
            </h2>
          </FadeSection>
          <div className="grid grid-cols-2 lg:grid-cols-4">
            {[
              { score: 92, name: 'Confidence', desc: 'How assured and steady you sound when making your point.' },
              { score: 85, name: 'Clarity', desc: 'How clearly you express your needs and boundaries.' },
              { score: 88, name: 'Tone', desc: 'Your emotional balance and empathy throughout.' },
              { score: 78, name: 'Effectiveness', desc: 'How well you move the conversation toward resolution.' },
            ].map((metric, i) => (
              <FadeSection key={i} delay={i * 0.1}>
                <div
                  className="text-center py-8 px-4"
                  style={{ borderRight: i < 3 ? '1px solid rgba(245,239,230,0.08)' : 'none' }}
                  data-testid={`metric-${metric.name.toLowerCase()}`}
                >
                  <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(48px, 6vw, 80px)', fontWeight: 300, color: '#D4A853', lineHeight: 1, display: 'block' }} data-testid={`metric-score-${metric.name.toLowerCase()}`}>
                    {metric.score}
                  </span>
                  <AnimatedRule targetWidth={Math.round(metric.score * 0.8)} />
                  <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: '20px', color: '#F5EFE6', marginBottom: '8px' }}>{metric.name}</h3>
                  <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '14px', color: 'rgba(245,239,230,0.5)', lineHeight: 1.6, maxWidth: '200px', margin: '0 auto' }}>{metric.desc}</p>
                </div>
              </FadeSection>
            ))}
          </div>
        </div>
      </section>

      {/* === SECTION C — HOW IT WORKS === */}
      <section className="py-24 lg:py-32" style={{ background: '#F5EFE6' }} data-testid="how-it-works-section">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <FadeSection>
            <p className="text-center mb-4" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.12em', color: '#C4613A' }}>How It Works</p>
            <h2 className="text-center mb-20" style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(32px, 5vw, 52px)', fontWeight: 300 }}>
              Three steps to a <span style={{ fontStyle: 'italic' }}>better conversation</span>
            </h2>
          </FadeSection>
          <div className="grid md:grid-cols-3 gap-12 items-start">
            {[
              { num: '01', title: 'Choose your scenario', desc: 'Pick from real situations — vacation budget, debt talk, salary negotiation — and set the tone for your session.', img: 'https://images.pexels.com/photos/5390120/pexels-photo-5390120.jpeg?auto=compress&cs=tinysrgb&w=800', delay: 0.1 },
              { num: '02', title: 'Practice with your AI partner', desc: 'Have the real conversation in a safe space. Speak naturally, take your time, and find the right words.', img: 'https://images.unsplash.com/photo-1543269865-cbf427effbad?auto=format&fit=crop&w=800&q=85', delay: 0.2 },
              { num: '03', title: 'Get coached in real time', desc: 'See what\'s working and what to try differently. Build the skills that last beyond the screen.', img: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=800&q=85', delay: 0.3 },
            ].map((step, i) => (
              <FadeSection key={i} delay={step.delay}>
                <div className="relative" data-testid={`step-${i + 1}`}>
                  <span
                    className="absolute z-10 pointer-events-none"
                    style={{
                      fontFamily: "'Playfair Display', serif",
                      fontSize: '96px',
                      fontWeight: 300,
                      color: 'rgba(212,168,83,0.25)',
                      lineHeight: 1,
                      top: '-12px',
                      left: '12px',
                    }}
                  >
                    {step.num}
                  </span>
                  <img
                    src={step.img}
                    alt={step.title}
                    className="w-full object-cover mb-6 relative z-0"
                    style={{ aspectRatio: '4/3', borderRadius: '12px' }}
                  />
                  <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: '24px', fontWeight: 600, marginBottom: '8px' }}>{step.title}</h3>
                  <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '15px', color: '#6B6B6B', lineHeight: 1.7 }}>{step.desc}</p>
                </div>
              </FadeSection>
            ))}
          </div>
        </div>
      </section>

      {/* === TESTIMONIALS — Deep green === */}
      <section className="py-24 lg:py-32" style={{ background: '#1C3D2E' }}>
        <div className="max-w-5xl mx-auto px-6 lg:px-12 space-y-20">
          {[
            {
              quote: 'We used to shut down every time money came up. After practicing here, I brought up our credit card debt and we actually made a plan together.',
              name: 'Sarah M.',
              role: 'Married 6 years',
              img: 'https://images.unsplash.com/photo-1770058428154-9eee8a6a1fbb?w=80&h=80&fit=crop&crop=face'
            },
            {
              quote: 'I was terrified to ask my parents about their retirement plan. This app helped me find the right words — and it went better than I ever expected.',
              name: 'Michael R.',
              role: 'First-generation planner',
              img: 'https://images.unsplash.com/photo-1651684215020-f7a5b6610f23?w=80&h=80&fit=crop&crop=face'
            },
            {
              quote: 'The coaching felt genuinely personal. It\'s not just a chatbot — it helped me understand why I avoid these conversations and how to change that.',
              name: 'Jennifer L.',
              role: 'Young professional',
              img: 'https://images.unsplash.com/photo-1745434159123-4908d0b9df94?w=80&h=80&fit=crop&crop=face'
            },
          ].map((t, i) => (
            <FadeSection key={i} delay={i * 0.1}>
              <div className="flex flex-col md:flex-row items-start gap-6 md:gap-10">
                <img src={t.img} alt={t.name} className="w-14 h-14 rounded-full object-cover flex-shrink-0" />
                <div>
                  <p className="text-xl sm:text-2xl italic leading-relaxed mb-4" style={{ fontFamily: "'Playfair Display', serif", color: '#F5EFE6' }}>
                    "{t.quote}"
                  </p>
                  <p className="text-sm font-medium" style={{ color: '#D4A853' }}>{t.name}</p>
                  <p className="text-sm" style={{ color: 'rgba(245,239,230,0.5)' }}>{t.role}</p>
                </div>
              </div>
            </FadeSection>
          ))}
        </div>
      </section>

      {/* === CTA FOOTER BAND === */}
      <section className="py-20 lg:py-24 text-center" style={{ background: '#C4613A' }}>
        <div className="max-w-2xl mx-auto px-6">
          <FadeSection>
            <h2 className="text-3xl sm:text-4xl font-semibold mb-6 text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
              Your next money conversation doesn't have to be hard.
            </h2>
            <Link to="/auth">
              <Button className="rounded-full px-8 py-6 text-base font-semibold shadow-none" style={{ background: '#F5EFE6', color: '#1C3D2E', fontFamily: "'DM Sans', sans-serif" }} data-testid="footer-cta">
                Start Free — No credit card needed <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </FadeSection>
        </div>
      </section>

      {/* === FOOTER === */}
      <footer className="py-10 border-t" style={{ background: '#F5EFE6', borderColor: 'rgba(26,26,26,0.08)' }}>
        <div className="max-w-6xl mx-auto px-6 lg:px-12 flex flex-col items-center gap-5">
          <div className="w-full flex flex-col sm:flex-row items-center justify-between gap-4">
            <img src="/logo.png" alt="Let's Talk Money" className="h-8 w-auto opacity-60" />
            <p className="text-sm" style={{ color: '#6B6B6B' }}>
              &copy; {new Date().getFullYear()} Let's Talk Money. All rights reserved.
            </p>
          </div>
          <a
            href="https://theaidept.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block"
            style={{ opacity: 0, animation: 'footerCreditFade 0.3s ease 0.2s forwards' }}
            onMouseEnter={e => { e.currentTarget.style.opacity = '0.7'; }}
            onMouseLeave={e => { e.currentTarget.style.opacity = '0.45'; }}
            data-testid="ai-dept-credit"
          >
            <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '12px', color: '#6B6B6B', letterSpacing: '0.01em' }}>
              Built with{' '}
              <span style={{ fontWeight: 600, color: '#4A4A4A' }}>The AI Dept.</span>
            </span>
          </a>
        </div>
        <style>{`@keyframes footerCreditFade { to { opacity: 0.45; } }`}</style>
      </footer>
    </div>
  );
}
